import React, { createContext, useContext, useState } from 'react';
import { Box } from '@mui/material';
import { StyledButton } from '../styles/StyledButtons';
import { StyledDocumentLink } from '../styles/StyledLink';
import StyledModal from '../styles/StyledModal';
import { StyledHeading, StyledTypography } from '../styles/styles';
import type { EOISettings } from './eoiTypes';

const defaultSettings: EOISettings = {
  downloadPDF: () => {},
  printMode: false,
  showDownloadPdf: true,
};

const EOISettingsContext = createContext<EOISettings>(defaultSettings);

const useEOISettingsContext = () => useContext(EOISettingsContext);

export const EOISettingsProvider = EOISettingsContext.Provider;

export const useEOISettings = (override?: Partial<EOISettings>): EOISettings => {
  const context = useEOISettingsContext();

  return {
    downloadPDF: override?.downloadPDF ?? context.downloadPDF,
    printMode: override?.printMode ?? context.printMode,
    showDownloadPdf: override?.showDownloadPdf ?? context.showDownloadPdf,
  };
};

export const EOITitle: React.FC<{ children?: string }> = ({
  children = 'Expression of Interest',
}) => {
  const { downloadPDF, printMode, showDownloadPdf } = useEOISettings();

  const showPdfButton = (showDownloadPdf ?? true) && !printMode;

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      mb={4}
      flexWrap="wrap"
      gap={2}
    >
      <StyledHeading className="react-EOITitle">{children}</StyledHeading>

      {showPdfButton ? (
        <Box display="flex" alignItems="center" gap={1} sx={{ px: { xs: '1.875rem', lg: 0 } }}>
          <StyledButton onClick={downloadPDF}>Download PDF</StyledButton>
        </Box>
      ) : null}
    </Box>
  );
};

interface EOITypographyProps {
  children: string | React.ReactNode | (string | React.ReactNode)[];
  fontWeight?: number;
  fontSize?: string | number;
  component?: string;
}

export const EOITypography: React.FC<EOITypographyProps> = ({
  children,
  fontWeight,
  component,
  fontSize,
}) => (
  <StyledTypography
    className="react-EOITypography"
    fontWeight={fontWeight}
    component={component}
    fontSize={fontSize}
    py="1.25rem"
  >
    {children}
  </StyledTypography>
);

const WholesaleInvestorModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  return (
    <StyledModal open={true} onClose={onClose}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>
        <StyledTypography>
          Wholesale Client as defined in Section 761G of the Corporations Act 2001, which requires a
          person or entity to have either:
        </StyledTypography>
        <StyledTypography>(1) Net assets of at least $2.5 million; or</StyledTypography>
        <StyledTypography>
          (2) Gross income of at least $250,000 for each of the last two financial years,
        </StyledTypography>
        <StyledTypography>
          to be verified through the investor obtaining a certificate (dated within the last 2 years
          and must match the name of the person and entity) from a qualified accountant.
        </StyledTypography>
        <StyledTypography>
          Alternatively, a person or entity is deemed a Wholesale Investor and does not need a
          certificate if they invest at least $500,000 into a single opportunity.
        </StyledTypography>
      </Box>
    </StyledModal>
  );
};

export const WholesaleInvestorStatement: React.FC = () => {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <EOITypography component="span" fontWeight={600}>
        Please note, investment is available to Wholesale Investors only
      </EOITypography>
      <EOITypography component="span" fontWeight={600}>
        {' '}
        (as defined in the{' '}
        <StyledDocumentLink onClick={() => setShowModal(true)}>Corporations Act</StyledDocumentLink>
        )
      </EOITypography>
      {showModal && <WholesaleInvestorModal onClose={() => setShowModal(false)} />}
    </>
  );
};

export const EOIIntroText: React.FC = () => (
  <EOITypography>
    Thank you for expressing interest in this Opportunity. <WholesaleInvestorStatement />
  </EOITypography>
);
